import { useState, useMemo } from 'react';
import { useNostr } from '@nostrify/react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FlagSVG, getFlagSVGString } from './FlagSVG';
import {
  hexToBytes, textToBytes, bytesToHex, bytesToFlagData, formatRemainder,
  generateId,
} from '@/lib/flagUtils';
import { createEphemeralKey, signWithEphemeralKey } from '@/lib/ephemeralKeys';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { FLAG_KIND } from '@/hooks/useFlags';
import { Loader2, Flag } from 'lucide-react';

interface FlagGeneratorProps {
  prefillHex?: string;
  prefillText?: string;
  prefillTitle?: string;
  prefillEncoding?: 'text' | 'hex';
  editDTag?: string;
  onPublished?: () => void;
}

export function FlagGenerator({
  prefillHex,
  prefillText,
  prefillTitle,
  prefillEncoding,
  editDTag,
  onPublished,
}: FlagGeneratorProps) {
  const [isHexMode, setIsHexMode] = useState(prefillEncoding === 'hex');
  const [textInput, setTextInput] = useState(prefillText ?? '');
  const [hexInput, setHexInput] = useState(prefillHex ?? '');
  const [title, setTitle] = useState(prefillTitle ?? '');
  const [loading, setLoading] = useState(false);
  const [hexError, setHexError] = useState('');

  const { nostr } = useNostr();
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();
  const { mutateAsync: publishEvent } = useNostrPublish();
  const { toast } = useToast();

  // Compute bytes in real-time
  const bytes = useMemo(() => {
    try {
      if (isHexMode) {
        if (!hexInput.trim()) return [];
        return hexToBytes(hexInput);
      } else {
        if (!textInput) return [];
        return textToBytes(textInput);
      }
    } catch {
      return [];
    }
  }, [isHexMode, hexInput, textInput]);

  const flagData = useMemo(() => bytes.length >= 3 ? bytesToFlagData(bytes) : null, [bytes]);
  const remainderText = flagData ? formatRemainder(flagData.remainder) : '';

  function validateHex(val: string) {
    setHexInput(val);
    try {
      if (val.trim()) hexToBytes(val);
      setHexError('');
    } catch (e) {
      setHexError((e as Error).message);
    }
  }

  async function handlePublish() {
    if (bytes.length < 3) {
      toast({ title: 'Not enough data', description: 'Enter at least 3 bytes to generate a flag.', variant: 'destructive' });
      return;
    }
    if (!title.trim()) {
      toast({ title: 'Title required', description: 'Please enter a title for your flag.', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const dTag = editDTag ?? generateId();
      const dataHex = bytesToHex(bytes);
      const encoding = isHexMode ? 'hex' : 'text';
      const source = isHexMode ? undefined : (textInput || undefined);

      const tags: string[][] = [
        ['d', dTag],
        ['title', title.trim()],
        ['data', dataHex],
        ['encoding', encoding],
        ['alt', `Free Speech Flag: ${title.trim()}`],
      ];
      if (source) tags.push(['source', source]);

      const eventTemplate = {
        kind: FLAG_KIND,
        content: title.trim(),
        tags,
        created_at: Math.floor(Date.now() / 1000),
      };

      if (user) {
        // Publish with logged-in user
        await publishEvent(eventTemplate);
      } else {
        // Publish with ephemeral key
        const ephKey = createEphemeralKey(dTag);
        const signed = signWithEphemeralKey(ephKey, eventTemplate);
        await nostr.event(signed, { signal: AbortSignal.timeout(8000) });
      }

      await queryClient.invalidateQueries({ queryKey: ['flags'] });
      toast({ title: editDTag ? 'Flag updated!' : 'Flag published!', description: title.trim() });

      if (!editDTag) {
        setTitle('');
        setTextInput('');
        setHexInput('');
      }
      onPublished?.();
    } catch (err) {
      toast({ title: 'Failed to publish', description: String(err), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }

  const canPublish = bytes.length >= 3 && title.trim().length > 0 && !hexError;

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Flag className="h-5 w-5 text-primary" />
          {editDTag ? 'Edit Flag' : 'Create a Free Speech Flag'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Mode toggle */}
        <div className="flex items-center gap-3">
          <Label htmlFor="mode-switch" className={!isHexMode ? 'font-semibold text-foreground' : 'text-muted-foreground'}>
            Text mode
          </Label>
          <Switch
            id="mode-switch"
            checked={isHexMode}
            onCheckedChange={setIsHexMode}
          />
          <Label htmlFor="mode-switch" className={isHexMode ? 'font-semibold text-foreground' : 'text-muted-foreground'}>
            Hex mode
          </Label>
        </div>

        {/* Input */}
        {isHexMode ? (
          <div>
            <Label htmlFor="hex-input">Hex byte sequence</Label>
            <Textarea
              id="hex-input"
              className="font-mono mt-1"
              placeholder="e.g. 09 F9 11 02 9D 74 E3 5B D8 41 56 C5 63 56 88 C0"
              value={hexInput}
              onChange={e => validateHex(e.target.value)}
              rows={3}
            />
            {hexError && <p className="text-destructive text-xs mt-1">{hexError}</p>}
            <p className="text-xs text-muted-foreground mt-1">
              Spaces, colons, and 0x prefixes are ignored.
            </p>
          </div>
        ) : (
          <div>
            <Label htmlFor="text-input">Text</Label>
            <Textarea
              id="text-input"
              className="mt-1"
              placeholder="Enter any text to encode as a flag…"
              value={textInput}
              onChange={e => setTextInput(e.target.value)}
              rows={3}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Text is UTF-8 encoded to bytes.
            </p>
          </div>
        )}

        {/* Byte info */}
        {bytes.length > 0 && (
          <div className="text-xs text-muted-foreground flex flex-wrap gap-3">
            <span>{bytes.length} byte{bytes.length !== 1 ? 's' : ''}</span>
            {flagData && <span>{flagData.stripes.length} stripe{flagData.stripes.length !== 1 ? 's' : ''}</span>}
            {remainderText && <span>Remainder: <code className="font-mono text-foreground">{remainderText}</code></span>}
          </div>
        )}

        {/* Preview */}
        <div className="rounded-lg overflow-hidden border border-border">
          <FlagSVG bytes={bytes} className="w-full" width={800} height={400} />
        </div>

        {/* Title */}
        <div>
          <Label htmlFor="title-input">Flag title</Label>
          <Input
            id="title-input"
            className="mt-1"
            placeholder="Give your flag a name…"
            value={title}
            onChange={e => setTitle(e.target.value)}
            maxLength={100}
          />
        </div>

        {/* Publish */}
        <Button
          onClick={handlePublish}
          disabled={!canPublish || loading}
          className="w-full"
        >
          {loading ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Publishing…</>
          ) : (
            user
              ? (editDTag ? 'Update flag' : 'Publish flag')
              : (editDTag ? 'Update flag (ephemeral key)' : 'Publish flag (ephemeral key)')
          )}
        </Button>
        {!user && (
          <p className="text-xs text-muted-foreground text-center">
            A new ephemeral keypair will be used. Log in to use your own identity.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
